import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { fahrtenService } from '../../../services/fahrtenService';
import { useAuth } from '../../../contexts/AuthContext';

const EditFahrtModal = ({ 
  isOpen, 
  onClose, 
  fahrt, 
  onFahrtUpdated,
  onFahrtDeleted 
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Form state
  const [vin, setVin] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [kennzeichen, setKennzeichen] = useState('');
  const [fahrtDatum, setFahrtDatum] = useState('');
  const [fahrtBeginn, setFahrtBeginn] = useState('');
  const [fahrtEnde, setFahrtEnde] = useState('');
  const [startziel, setStartziel] = useState('');
  const [ueber, setUeber] = useState('');
  const [endziel, setEndziel] = useState('');
  const [fahrtzweck, setFahrtzweck] = useState('');
  const [fahrerName, setFahrerName] = useState('');
  const [fahrerAdresse, setFahrerAdresse] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');

  // Fahrtzweck options
  const fahrtzweckOptions = [
    { value: 'innen-aussenreinigung', label: 'Innen-aussenreinigung' },
    { value: 'zurueck-gefahren-fertig-sauber', label: 'Zurück gefahren fertig mit Sauber machen' },
    { value: 'abholung-reinigung', label: 'Abholung für Reinigung' },
    { value: 'zurueck-gefahren-fertig-pkw', label: 'Zurück gefahren fertiger PKW' }
  ];

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Ausstehend' },
    { value: 'in_progress', label: 'In Bearbeitung' },
    { value: 'completed', label: 'Abgeschlossen' },
    { value: 'cancelled', label: 'Storniert' }
  ];

  // Load fahrt data when modal opens
  useEffect(() => {
    if (isOpen && fahrt) {
      setVin(fahrt.vin || '');
      setVehicleMake(fahrt.vehicle_make || '');
      setVehicleModel(fahrt.vehicle_model || '');
      setKennzeichen(localStorage.getItem('fahrt_kennzeichen') || '');
      setFahrtDatum(fahrt.fahrt_datum || '');
      setFahrtBeginn(fahrt.fahrt_uhrzeit_beginn || '');
      setFahrtEnde(fahrt.fahrt_uhrzeit_ende || '');
      setStartziel(fahrt.startziel || '');
      setUeber(fahrt.ueber || '');
      setEndziel(fahrt.endziel || '');
      setFahrtzweck(fahrt.fahrtzweck || '');
      setFahrerName(fahrt.fahrer_name || '');
      setFahrerAdresse(fahrt.fahrer_adresse || '');
      setNotes(fahrt.notes || '');
      setStatus(fahrt.status || 'pending');
    }
  }, [isOpen, fahrt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!fahrtzweck) {
      alert('Bitte wählen Sie einen Fahrtzweck aus.');
      return;
    }

    setIsLoading(true);
    
    try {
      const updateData = {
        vin: vin,
        vehicle_make: vehicleMake,
        vehicle_model: vehicleModel,
        fahrt_datum: fahrtDatum,
        fahrt_uhrzeit_beginn: fahrtBeginn,
        fahrt_uhrzeit_ende: fahrtEnde || null,
        startziel,
        ueber,
        endziel,
        fahrtzweck,
        fahrer_name: fahrerName,
        fahrer_adresse: fahrerAdresse,
        notes,
        status
      };

      // Save kennzeichen to localStorage
      if (kennzeichen) {
        localStorage.setItem('fahrt_kennzeichen', kennzeichen);
      }

      const { data, error } = await fahrtenService.updateFahrt(fahrt.id, updateData, user.id);
      
      if (error) {
        throw new Error(`Fehler beim Aktualisieren: ${error.message}`);
      }

      alert('Fahrt erfolgreich aktualisiert!');
      onFahrtUpdated(data);
      onClose();
    } catch (error) {
      console.error('Error updating fahrt:', error);
      alert(`Fehler beim Aktualisieren: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Sind Sie sicher, dass Sie diese Fahrt löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setIsDeleting(true);
    
    try {
      const { error } = await fahrtenService.deleteFahrt(fahrt.id, user.id);
      
      if (error) {
        throw new Error(`Fehler beim Löschen: ${error.message}`);
      }

      alert('Fahrt erfolgreich gelöscht!');
      onFahrtDeleted(fahrt.id);
      onClose();
    } catch (error) {
      console.error('Error deleting fahrt:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isLoading && !isDeleting) {
      onClose();
    }
  };

  if (!isOpen || !fahrt) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground">Fahrt bearbeiten</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {vehicleMake} {vehicleModel} - {vin}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isLoading || isDeleting}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Vehicle Info - Editable */}
        <div className="p-6 border-b border-border bg-muted/30">
          <h3 className="text-sm font-semibold text-foreground mb-3">Fahrzeugdaten</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                VIN
              </label>
              <Input
                type="text"
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase())}
                placeholder="17-stellige VIN"
                disabled={isLoading || isDeleting}
                maxLength={17}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Kennzeichen
              </label>
              <Input
                type="text"
                value={kennzeichen}
                onChange={(e) => setKennzeichen(e.target.value.toUpperCase())}
                placeholder="z.B. K-AB 1234"
                disabled={isLoading || isDeleting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Fahrzeugmarke
              </label>
              <Input
                type="text"
                value={vehicleMake}
                onChange={(e) => setVehicleMake(e.target.value)}
                placeholder="z.B. BMW, Audi, Mercedes"
                disabled={isLoading || isDeleting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Fahrzeugmodell
              </label>
              <Input
                type="text"
                value={vehicleModel}
                onChange={(e) => setVehicleModel(e.target.value)}
                placeholder="z.B. 3er, A4, C-Klasse"
                disabled={isLoading || isDeleting}
              />
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Datum *
              </label>
              <Input
                type="date"
                value={fahrtDatum}
                onChange={(e) => setFahrtDatum(e.target.value)}
                disabled={isLoading || isDeleting}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Uhrzeit Beginn *
              </label>
              <Input
                type="time"
                value={fahrtBeginn}
                onChange={(e) => setFahrtBeginn(e.target.value)}
                disabled={isLoading || isDeleting}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Uhrzeit Ende
              </label>
              <Input
                type="time"
                value={fahrtEnde}
                onChange={(e) => setFahrtEnde(e.target.value)}
                disabled={isLoading || isDeleting}
              />
            </div>
          </div>

          {/* Route */}
          <div>
            <h4 className="text-md font-medium text-foreground mb-3">Fahrtstrecke</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Startziel
                </label>
                <Input
                  type="text"
                  value={startziel}
                  onChange={(e) => setStartziel(e.target.value)}
                  placeholder="Startort eingeben"
                  disabled={isLoading || isDeleting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  über
                </label>
                <Input
                  type="text"
                  value={ueber}
                  onChange={(e) => setUeber(e.target.value)}
                  placeholder="Zwischenstopp (optional)"
                  disabled={isLoading || isDeleting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Endziel
                </label>
                <Input
                  type="text"
                  value={endziel}
                  onChange={(e) => setEndziel(e.target.value)}
                  placeholder="Zielort eingeben"
                  disabled={isLoading || isDeleting}
                />
              </div>
            </div>
          </div>

          {/* Purpose and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Fahrtzweck *
              </label>
              <Select
                value={fahrtzweck}
                onChange={setFahrtzweck}
                placeholder="Fahrtzweck auswählen"
                disabled={isLoading || isDeleting}
                options={fahrtzweckOptions}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={status}
                onChange={setStatus}
                placeholder="Status auswählen"
                disabled={isLoading || isDeleting}
                options={statusOptions}
              />
            </div>
          </div>

          {/* Driver */}
          <div>
            <h4 className="text-md font-medium text-foreground mb-3">Fahrzeugführer</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Name, Vorname
                </label>
                <Input
                  type="text"
                  value={fahrerName}
                  onChange={(e) => setFahrerName(e.target.value)}
                  placeholder="Nachname, Vorname"
                  disabled={isLoading || isDeleting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  PLZ, Ort, Straße
                </label>
                <Input
                  type="text"
                  value={fahrerAdresse}
                  onChange={(e) => setFahrerAdresse(e.target.value)}
                  placeholder="PLZ, Ort, Straße"
                  disabled={isLoading || isDeleting}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notizen
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Informationen..."
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              rows={3}
              disabled={isLoading || isDeleting}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-border">
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              loading={isDeleting}
              disabled={isLoading}
              iconName="Trash2"
              iconPosition="left"
            >
              Löschen
            </Button>
            
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading || isDeleting}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                disabled={!fahrtzweck || isDeleting}
                iconName="Save"
                iconPosition="left"
              >
                Speichern
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFahrtModal;
